import type { LoginUser } from '@/api/auth/types';
import { defineStore } from 'pinia';
import { useRouter } from 'vue-router';
import { getMe } from '@/api/apps';
import { EMBED_BRIDGE_PATH } from '@/config';
import { useAgentStore } from './agent';
import { useDevToolsStore } from './devTools';
import { useNotificationStore } from './notification';
import { useSessionStore } from './session';

export const useUserStore = defineStore(
  'user',
  () => {
    const token = ref<string>();
    const router = useRouter();
    const setToken = (value: string) => {
      token.value = value;
    };
    const clearToken = () => {
      token.value = void 0;
    };

    const userInfo = ref<LoginUser>();
    const setUserInfo = (value: LoginUser) => {
      userInfo.value = value;
    };
    const clearUserInfo = () => {
      userInfo.value = void 0;
    };

    // Admin flag + canonical user id, resolved from the Controller's /auth/me
    // (the validated ruoyi token), not the YY SSO response — SSO does not carry
    // roles. Both persist across reloads; userIdString is the stringified YY
    // Long (19-digit ids overflow Number, so never use userInfo.userId for it).
    const isAdmin = ref(false);
    const userIdString = ref<string>();
    // Guard so fetchMe fires at most once until it has resolved an identity.
    let meResolved = false;

    const fetchMe = async () => {
      if (!token.value || meResolved)
        return;
      try {
        const me = await getMe();
        isAdmin.value = !!me.is_admin;
        userIdString.value = me.user_id;
        meResolved = true;
      }
      catch {
        // Degrade closed: keep isAdmin=false so admin-only UI stays hidden. The
        // next authed navigation retries (meResolved stays false).
      }
    };

    const logout = async () => {
      // 清空 token 和用户信息
      clearToken();
      clearUserInfo();
      isAdmin.value = false;
      userIdString.value = void 0;
      meResolved = false;

      // 清空所有相关 store 的数据
      const agentStore = useAgentStore();
      const sessionStore = useSessionStore();
      const notificationStore = useNotificationStore();

      // 清空智能体数据
      agentStore.agents = [];
      agentStore.clearCurrentAgent();

      // 清空会话数据
      sessionStore.sessionList = [];
      sessionStore.setCurrentSession(null);

      // 清空通知数据
      notificationStore.resetUnreadCount();

      useDevToolsStore().reset();

      // 小程序注入页不能被踢去登录，否则 web-view 里只剩登录表单
      if (router.currentRoute.value.path === EMBED_BRIDGE_PATH)
        return;

      // token 失效时，优先跳转到登录页；同时也允许业务侧用弹窗登录
      router.replace({ path: '/login' });
    };

    // 新增：登录弹框状态
    const isLoginDialogVisible = ref(false);

    // 新增：打开弹框方法
    const openLoginDialog = () => {
      isLoginDialogVisible.value = true;
    };

    // 新增：关闭弹框方法（可根据需求扩展）
    const closeLoginDialog = () => {
      isLoginDialogVisible.value = false;
    };

    return {
      token,
      setToken,
      clearToken,
      userInfo,
      setUserInfo,
      clearUserInfo,
      logout,
      isAdmin,
      userIdString,
      fetchMe,
      // 新增：暴露弹框状态和方法
      isLoginDialogVisible,
      openLoginDialog,
      closeLoginDialog,
    };
  },
  {
    persist: true,
  },
);
